import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import { createExpediente } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft } from "@phosphor-icons/react";

export default function NewExpediente() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [numero, setNumero] = useState("");
  const [titulo, setTitulo] = useState("");
  const [nombre, setNombre] = useState("");
  const [tipoTramite, setTipoTramite] = useState("deslinde");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    let numeroFinal = numero.trim();
    if (!numeroFinal) {
      const d = new Date();
      const pad = (n) => String(n).padStart(2, "0");
      numeroFinal = `SIN-N°-${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}`;
    }
    setSubmitting(true);
    try {
      const exp = await createExpediente({
        numero_expediente: numeroFinal,
        titulo_expediente: titulo.trim(),
        tipo_tramite: tipoTramite,
        cliente: nombre.trim() ? { nombre: nombre.trim() } : null,
      });
      toast.success(t.saved);
      navigate(`/expediente/${exp.id}`);
    } catch (err) {
      toast.error(t.error);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="p-6 md:p-10 max-w-2xl mx-auto" data-testid="new-page">
      <button
        data-testid="back-btn"
        onClick={() => navigate("/")}
        className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-6"
      >
        <ArrowLeft size={14} /> {t.back}
      </button>
      <div className="coord-label mb-2">+ NEW CASE</div>
      <h1 className="text-3xl font-black tracking-tighter text-slate-900 mb-8">{t.new_expediente}</h1>

      <form onSubmit={onSubmit} className="bg-white border border-slate-200 p-6 space-y-5">
        {/* Selector tipo de trámite */}
        <div data-testid="tipo-tramite-selector">
          <Label className="coord-label">TIPO DE TRÁMITE</Label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2">
            <button
              type="button"
              data-testid="tipo-deslinde"
              onClick={() => setTipoTramite("deslinde")}
              className={`text-left p-3 border-2 rounded-sm transition-all ${
                tipoTramite === "deslinde"
                  ? "border-orange-500 bg-orange-50"
                  : "border-slate-200 hover:border-slate-400"
              }`}
            >
              <div className="font-bold text-sm text-slate-900">DESLINDE / MENSURA</div>
              <div className="text-xs text-slate-500 mt-1">
                Trámite catastral con DRMC: oficios, acta de hitos, informe técnico.
              </div>
            </button>
            <button
              type="button"
              data-testid="tipo-duplicado"
              onClick={() => setTipoTramite("duplicado_perdida")}
              className={`text-left p-3 border-2 rounded-sm transition-all ${
                tipoTramite === "duplicado_perdida"
                  ? "border-orange-500 bg-orange-50"
                  : "border-slate-200 hover:border-slate-400"
              }`}
            >
              <div className="font-bold text-sm text-slate-900">DUPLICADO POR PÉRDIDA</div>
              <div className="text-xs text-slate-500 mt-1">
                Duplicado de Certificado de Título: instancia, declaración jurada, compulsa, publicación.
              </div>
            </button>
            <button
              type="button"
              data-testid="tipo-posesion"
              onClick={() => setTipoTramite("posesion_mejora")}
              className={`text-left p-3 border-2 rounded-sm transition-all ${
                tipoTramite === "posesion_mejora"
                  ? "border-orange-500 bg-orange-50"
                  : "border-slate-200 hover:border-slate-400"
              }`}
            >
              <div className="font-bold text-sm text-slate-900">POSESIÓN / MEJORA</div>
              <div className="text-xs text-slate-500 mt-1">
                Declaración Jurada de Posesión y Mejora: 7 testigos, propietarios y compulsa.
              </div>
            </button>
          </div>
        </div>

        <div>
          <Label className="coord-label" htmlFor="numero">{t.numero_expediente}</Label>
          <Input
            id="numero"
            data-testid="input-numero"
            value={numero}
            onChange={(e) => setNumero(e.target.value)}
            className="mt-1.5 rounded-sm font-mono"
            placeholder="Opcional — se asigna luego cuando lo otorgue Mensuras"
          />
          <p className="text-xs text-slate-500 mt-1.5">Puedes dejarlo en blanco. Se generará un identificador temporal y lo cambiarás cuando recibas el número oficial.</p>
        </div>
        <div>
          <Label className="coord-label" htmlFor="titulo">{t.titulo_expediente}</Label>
          <Input
            id="titulo"
            data-testid="input-titulo"
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            className="mt-1.5 rounded-sm"
            placeholder="Parcela 123 - Santiago"
          />
        </div>
        <div>
          <Label className="coord-label" htmlFor="nombre">{t.cliente_nombre}</Label>
          <Input
            id="nombre"
            data-testid="input-cliente-nombre"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            className="mt-1.5 rounded-sm"
          />
        </div>
        <div className="flex gap-2 pt-3 border-t border-slate-200">
          <Button
            type="submit"
            data-testid="submit-new-btn"
            disabled={submitting}
            className="rounded-sm bg-orange-500 hover:bg-orange-400 text-white"
          >
            {submitting ? "…" : t.create}
          </Button>
          <Button
            type="button"
            data-testid="cancel-new-btn"
            variant="outline"
            onClick={() => navigate("/")}
            className="rounded-sm"
          >{t.cancel}</Button>
        </div>
      </form>
    </div>
  );
}
